import { useEffect, useMemo } from 'react';
import throttle from 'lodash/throttle';
import {
  Channels,
  SeriesPrivateEvents,
  useSubscribe,
} from '@mentimeter/realtime';
import { captureException } from '@mentimeter/errors/sentry';
import { MentiError } from '@mentimeter/error-utils/error';
import type { Reaction } from '@mentimeter/http-clients';
import { useRealtimeReactionsStore } from './use-realtime-reactions-store';

const REACTION_THROTTLE = 25;
const EMOJIS = ['heart1', 'cat', 'thumbsup', 'thumbsdown', 'question'];

function assertIsReaction(value: string): asserts value is Reaction {
  if (!EMOJIS.includes(value)) throw new Error(`${value} is not a reaction`);
}

export const useSubscribeRealtimeReaction = (
  voteKey: string,
  shouldSubscribe: boolean,
) => {
  const addReaction = useRealtimeReactionsStore((state) => state.addReaction);
  const clearReactions = useRealtimeReactionsStore(
    (state) => state.clearReactions,
  );

  const throttledAddReaction = useMemo(
    () => throttle(addReaction, REACTION_THROTTLE),
    [addReaction],
  );

  useEffect(clearReactions, [voteKey, clearReactions]);

  useSubscribe(
    {
      channel: Channels.SERIES_PRIVATE,
      value: voteKey,
      shouldSubscribe,
    },
    SeriesPrivateEvents.REACTION,
    (msg) => {
      try {
        const { emoji, amount, color } = msg.data.payload;
        assertIsReaction(emoji);
        const colorIndex = parseInt(color.slice(-1), 10) || 0;

        throttledAddReaction(emoji, {
          timestamp: Date.now(),
          amount,
          colorIndex,
        });
      } catch (error) {
        captureException(
          new MentiError('Realtime reactions subscription error', {
            cause: error,
            feature: 'live',
          }),
        );
      }
    },
  );
};
